import { useMutation } from '@apollo/client/react';
import { logError } from 'fergy-core-react-logging';
import { type FunctionComponent } from 'react';
import { useForm } from 'react-hook-form';
import {
	type SubscribeToNewsletterMutation,
	type SubscribeToNewsletterMutationVariables
} from '../../../__generated__/graphql-client-types';
import { SUCCESS_SUBSCRIBED, ERROR_SUBSCRIBE } from '../../../constants/message';
import { generateCloudinaryUrl } from '../../../helpers/cloudinary-helper/cloudinary-helper';
import { emailInputRegistrationConfig } from '../../../helpers/form-validation-helper/form-validation-helper';
import { useNotification } from '../../../hooks/notification/notification.hooks';
import { useRecaptcha } from '../../../hooks/recaptcha/recaptcha.hooks';
import { SUBSCRIBE_TO_NEWSLETTER } from '../../../queries/newsletter/newsletter.queries';
import { StyledButton } from '../../buttons';
import { Recaptcha } from '../../common-components/recaptcha/recaptcha.component';
import { TextInput } from '../../inputs';
import { InputGroup } from '../../inputs/input-group/input-group.component';
import CancelIcon from '../../svgs/cancel.svg';

/**
 * Keys representing the inputs' `name` attributes
 */
type FormInputs = {
	email: string;
};

const backgroundImage = generateCloudinaryUrl('mediabase/site/build.com/images/learning-center/diningroom', { width: 404, height: 312 });

export const LearningCenterEmailTile: FunctionComponent = () => {
	const { getToken } = useRecaptcha('newsletter');
	const { notifySuccess, notifyWarning } = useNotification();
	const [subscribeToNewsletter] = useMutation<SubscribeToNewsletterMutation, SubscribeToNewsletterMutationVariables>(
		SUBSCRIBE_TO_NEWSLETTER
	);

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset
	} = useForm<FormInputs>();

	const onSubmit = async (formData: FormInputs) => {
		const token = await getToken();
		subscribeToNewsletter({
			variables: {
				input: {
					email: formData.email,
					origin: 'FOOTER_ENTRY',
					token
				}
			}
		})
			.then((result) => notifySuccess(result?.data?.subscribeToNewsletter || SUCCESS_SUBSCRIBED))
			.catch((error) => {
				logError(error);
				notifyWarning(ERROR_SUBSCRIBE, 3000);
			})
			.finally(reset);
	};

	return (
		<div className="w-100 w-50-ns pa2">
			<div className="aspect-ratio aspect-ratio--4x3 aspect-ratio--6x4-ns">
				<div
					className="aspect-ratio--object flex justify-center items-center contain"
					style={{ backgroundImage: `url(${backgroundImage})` }}>
					<form
						onSubmit={handleSubmit(onSubmit)}
						aria-label="newsletter signup form"
						className="w-80 ph2 pv3 ph4-ns pv5-ns flex-column justify-center items-center tc bg-theme-white-o80">
						<div className="mb2 f3 fw6">Need Inspiration?</div>
						<div className="mb2 f6 lh-copy">Subscribe now for coupons, exclusive deals, and design inspiration.</div>
						<InputGroup className="w-100">
							<TextInput
								ariaLabel="email"
								placeholder="Email Address"
								autoCapitalize="off"
								{...register('email', emailInputRegistrationConfig)}
							/>
							<StyledButton buttonType="submit">Subscribe</StyledButton>
						</InputGroup>
						{/* Input Group doesn't support form invalid state */}
						<div className={`flex flex-row items-center mt3 f6 theme-error`} style={{ minHeight: '1.25rem' }}>
							{errors.email && (
								<>
									<CancelIcon className="f4" />
									{errors.email?.message}
								</>
							)}
						</div>
						<Recaptcha />
					</form>
				</div>
			</div>
		</div>
	);
};
